import { useState, useRef, useEffect, useCallback } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Link, useNavigate } from 'react-router-dom'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/authStore'
import { useRecommendationsStore } from '@/stores/recommendationsStore'
import { TENANT_LANDING } from '@/config/tenantLanding'
import { WaitlistExpirationNotices } from '@/components/WaitlistExpirationNotices'
import { BRANDING } from '@/config/branding'
import { semanticSearchService, ChatMessage } from '@/services/semanticSearchService'
import { registrationService } from '@/services/registrationService'
import { getInProgressOnlineCourses } from '@/lib/onlineCourseStatus'
import { 
  BookOpen, 
  Award, 
  FileText, 
  Users, 
  TrendingUp, 
  Library,
  Calendar,
  Sparkles,
  ArrowUp,
  Trash2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Clock,
  MapPin,
  PlayCircle,
  ArrowRight
} from 'lucide-react'

interface SearchHistoryItem {
  query: string
  response: string
  timestamp: Date
  isStreaming?: boolean
}

const IN_PROGRESS_PROMPT_DISMISSED_KEY = 'inProgressCoursePromptDismissed'

// Storage helpers
function getChatHistory(): ChatMessage[] {
  try {
    const stored = sessionStorage.getItem('chatHistory')
    return stored ? JSON.parse(stored) : []
  } catch (error) {
    return []
  }
}

function saveChatHistory(history: ChatMessage[]) {
  try {
    const trimmedHistory = history.slice(-10)
    sessionStorage.setItem('chatHistory', JSON.stringify(trimmedHistory))
  } catch (error) {
    console.error('Error saving chat history:', error)
  }
}

export function Landing() {
  const navigate = useNavigate()
  const { t } = useTranslation('dashboard')
  const { isAuthenticated, user } = useAuthStore()

  // AI Chat state
  const [searchQuery, setSearchQuery] = useState('')
  const [searchHistory, setSearchHistory] = useState<SearchHistoryItem[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [isNearBottom, setIsNearBottom] = useState(true)
  const [userHasScrolled, setUserHasScrolled] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const resultsEndRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // Recommendations state
  const { recommendations, isLoading: loadingRecommendations } = useRecommendationsStore()
  const carouselRef = useRef<HTMLDivElement>(null)
  const [showLeftArrow, setShowLeftArrow] = useState(false)
  const [showRightArrow, setShowRightArrow] = useState(true)
  const { enableRecommendations: recommendationsFeatureFlag = true, externalCatalogUrl: catalogFlagOn = false } = useFlags()
  const externalCatalogUrl = catalogFlagOn ? TENANT_LANDING.externalCatalogUrl : null
  const [userRecommendationsEnabled, setUserRecommendationsEnabled] = useState(() => {
    const saved = localStorage.getItem('enableRecommendations')
    return saved !== null ? JSON.parse(saved) : true
  })
  const enableRecommendations = recommendationsFeatureFlag && userRecommendationsEnabled
  const navyLanding = Boolean(TENANT_LANDING.landingWordmark)
  const [isCarouselCollapsed, setIsCarouselCollapsed] = useState(() => {
    const saved = localStorage.getItem('carouselCollapsed')
    return saved !== null ? JSON.parse(saved) : false
  })
  
  // Get time-appropriate greeting
  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return t('goodMorning')
    if (hour < 18) return t('goodAfternoon')
    return t('goodEvening')
  }
  
  // Get user's first name or fallback
  const getUserName = () => {
    if (!user) return ''
    // Use firstName if available
    if (user.firstName) return user.firstName
    // Use name only if it's a real name (not an email address)
    if (user.name && typeof user.name === 'string' && !user.name.includes('@')) {
      return user.name.split(' ')[0] || ''
    }
    // Nothing to show — don't fall back to email as a display name
    return ''
  }

  const toggleCarouselCollapse = () => {
    const newState = !isCarouselCollapsed
    setIsCarouselCollapsed(newState)
    localStorage.setItem('carouselCollapsed', JSON.stringify(newState))
  }


  const scroll = (direction: 'left' | 'right') => {
    if (carouselRef.current) {
      const scrollAmount = 400
      const newScrollPosition = carouselRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount)
      carouselRef.current.scrollTo({ left: newScrollPosition, behavior: 'smooth' })
      
      setTimeout(() => {
        if (carouselRef.current) {
          setShowLeftArrow(carouselRef.current.scrollLeft > 0)
          setShowRightArrow(
            carouselRef.current.scrollLeft < carouselRef.current.scrollWidth - carouselRef.current.clientWidth - 10
          )
        }
      }, 300)
    }
  }

  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current) return
    
    setUserHasScrolled(true)
    const container = scrollContainerRef.current
    const scrollTop = container.scrollTop
    const scrollHeight = container.scrollHeight
    const clientHeight = container.clientHeight
    const bottomThreshold = 50
    
    const isNear = scrollHeight - scrollTop - clientHeight < bottomThreshold
    setIsNearBottom(isNear)
  }, [])

  const scrollToBottom = useCallback((behavior: 'auto' | 'smooth' = 'smooth') => {
    setTimeout(() => {
      resultsEndRef.current?.scrollIntoView({ behavior, block: 'end' })
    }, 100)
  }, [])

  useEffect(() => {
    if (searchHistory.length > 0) {
      if (!userHasScrolled || isNearBottom) {
        scrollToBottom('smooth')
      }
    }
  }, [searchHistory, userHasScrolled, isNearBottom, scrollToBottom])

  const [inProgressCourses, setInProgressCourses] = useState<ReturnType<typeof getInProgressOnlineCourses> | null>(null)
  const [inProgressPromptDismissed, setInProgressPromptDismissed] = useState(
    () => sessionStorage.getItem(IN_PROGRESS_PROMPT_DISMISSED_KEY) === 'true',
  )

  // Cached history only: a forced reload rebuilds the user's whole history cache.
  useEffect(() => {
    if (!isAuthenticated) {
      setInProgressCourses(null)
      return
    }
    let cancelled = false
    registrationService.getRegistrationHistory()
      .then(history => { if (!cancelled) setInProgressCourses(getInProgressOnlineCourses(history)) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [isAuthenticated])

  const inProgressCourseList = inProgressCourses ? [...inProgressCourses.online, ...inProgressCourses.canvas] : []
  const showInProgressPrompt = inProgressCourseList.length > 0 && !inProgressPromptDismissed
  const continueCoursesPath = inProgressCourses?.online.length
    ? '/registration-history?tab=online'
    : '/registration-history?tab=canvas'

  const dismissInProgressPrompt = () => {
    sessionStorage.setItem(IN_PROGRESS_PROMPT_DISMISSED_KEY, 'true')
    setInProgressPromptDismissed(true)
  }

  // Sync recommendations preference when toggled from UserProfile
  useEffect(() => {
    const handleToggle = (e: Event) => {
      const enabled = (e as CustomEvent<{ enabled: boolean }>).detail.enabled
      setUserRecommendationsEnabled(enabled)
    }
    window.addEventListener('recommendationsToggle', handleToggle)
    return () => window.removeEventListener('recommendationsToggle', handleToggle)
  }, [])

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  const formatTime = (startDate: string | null | undefined, endDate: string | null | undefined) => {
    const fmt = (s: string | null | undefined) => {
      if (!s) return ''
      const timePart = s.split('T')[1]?.split('.')[0] || ''
      const [hour, minute] = timePart.split(':').slice(0, 2)
      const h = parseInt(hour)
      if (isNaN(h)) return ''
      const ampm = h >= 12 ? 'PM' : 'AM'
      const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h
      return `${displayHour}:${minute} ${ampm}`
    }
    const start = fmt(startDate)
    const end = fmt(endDate)
    if (start && end) return `${start} - ${end}`
    return start || end || ''
  }

  const getCategoryColor = (category: string) => {
    const categories = [
      { bg: 'bg-primary-100', text: 'text-primary-700' },
      { bg: 'bg-secondary-100', text: 'text-secondary-700' },
      { bg: 'bg-maroon-100', text: 'text-maroon-700' },
      { bg: 'bg-[#052B3C]/10', text: 'text-[#052B3C]' },
      { bg: 'bg-[#064A71]/10', text: 'text-[#064A71]' },
      { bg: 'bg-[#004D4D]/10', text: 'text-[#004D4D]' },
    ]
    
    let hash = 0
    for (let i = 0; i < category.length; i++) {
      hash = ((hash << 5) - hash) + category.charCodeAt(i)
      hash = hash & hash
    }
    const index = Math.abs(hash) % categories.length
    
    return categories[index]
  }

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!searchQuery.trim() || isSearching) return

    const currentQuery = searchQuery.trim()
    setSearchQuery('')
    setIsSearching(true)
    setUserHasScrolled(false)
    
    setSearchHistory(prev => [...prev, {
      query: currentQuery,
      response: '',
      timestamp: new Date(),
      isStreaming: true
    }])
    
    setTimeout(() => scrollToBottom('smooth'), 100)
    
    try {
      let chatHistory = getChatHistory()
      chatHistory = chatHistory.filter(msg => msg.content && msg.content.trim())
      chatHistory.push({ role: 'user', content: currentQuery })
      
      let fullResponse = ''
      let streamingSucceeded = false
      
      try {
        await semanticSearchService.searchStream(
          currentQuery,
          chatHistory,
          5,
          (chunk: string) => {
            streamingSucceeded = true
            fullResponse += chunk
            
            setSearchHistory(prev => {
              const newHistory = [...prev]
              const lastIndex = prev.findIndex(item => item.query === currentQuery && item.isStreaming !== false)
              if (lastIndex >= 0) {
                newHistory[lastIndex] = {
                  ...newHistory[lastIndex],
                  response: fullResponse,
                  isStreaming: true
                }
              }
              return newHistory
            })
          },
          () => {
            setSearchHistory(prev => {
              const newHistory = [...prev]
              const lastIndex = prev.findIndex(item => item.query === currentQuery)
              if (lastIndex >= 0) {
                newHistory[lastIndex] = {
                  ...newHistory[lastIndex],
                  response: fullResponse,
                  isStreaming: false
                }
              }
              return newHistory
            })
            
            chatHistory.push({ role: 'assistant', content: fullResponse })
            saveChatHistory(chatHistory)
            setIsSearching(false)
          },
          () => {
          }
        )
      } catch (streamError) {
        console.error('Error streaming chat response:', streamError)
      }
      
      if (!streamingSucceeded) {
        try {
          const response = await semanticSearchService.search(currentQuery, chatHistory, 5)
          
          let responseText = ''
          
          if (typeof response === 'string') {
            responseText = response
          } else if (response && typeof response === 'object') {
            responseText = response?.message || response?.text || response?.response || response?.answer || ''
          }
          
          chatHistory.push({ role: 'assistant', content: responseText })
          saveChatHistory(chatHistory)
          
          setSearchHistory(prev => {
            const newHistory = [...prev]
            const lastIndex = prev.findIndex(item => item.query === currentQuery)
            if (lastIndex >= 0) {
              newHistory[lastIndex] = {
                ...newHistory[lastIndex],
                response: responseText,
                isStreaming: false
              }
            }
            return newHistory
          })
          
          setIsSearching(false)
        } catch (fallbackError) {
          const errorMessage = t('errorProcessingRequest')
          
          setSearchHistory(prev => {
            const newHistory = [...prev]
            const lastIndex = prev.findIndex(item => item.query === currentQuery)
            if (lastIndex >= 0) {
              newHistory[lastIndex] = {
                ...newHistory[lastIndex],
                response: errorMessage,
                isStreaming: false
              }
            }
            return newHistory
          })
          
          setIsSearching(false)
        }
      }
    } catch (error) {
      const errorMessage = t('errorProcessingRequest')
      
      setSearchHistory(prev => {
        const newHistory = [...prev]
        const lastIndex = prev.findIndex(item => item.query === currentQuery)
        if (lastIndex >= 0) {
          newHistory[lastIndex] = {
            ...newHistory[lastIndex],
            response: errorMessage,
            isStreaming: false
          }
        }
        return newHistory
      })
      
      setIsSearching(false)
    }
  }

  const handleClearChat = () => {
    setSearchHistory([])
    sessionStorage.removeItem('chatHistory')
    setSearchQuery('')
    setUserHasScrolled(false)
    setIsNearBottom(true)
  }

  // Pre-process: convert session IDs to markdown links
  // Handles both [1234567] bracket notation and bare numbers not already in a link
  const linkifySessionIds = (text: string): string =>
    text
      // [ID] not already a markdown link → [ID](/session/ID)
      .replace(/\[(\d{5,8})\](?!\()/g, '[$1](/session/$1)')
      // bare ID not preceded by [, /, or a digit (i.e. not already inside a link)
      .replace(/(?<![[\]/\d])(\d{5,8})(?![/\]\d])/g, '[$1](/session/$1)')

  const renderResponseWithLinks = (text: string) => (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        // Session-ID links and regular hrefs both route through navigate
        a: ({ href, children }) => {
          const sessionMatch = href?.match(/^\/session\/(\d+)$/)
          return (
            <a
              href={href}
              onClick={(e) => { e.preventDefault(); navigate(href || '/') }}
              className={sessionMatch
                ? 'text-primary-600 hover:underline cursor-pointer font-medium'
                : 'text-primary-600 hover:underline cursor-pointer'}
            >
              {children}
            </a>
          )
        },
        // Styled markdown elements
        h1: ({ children }) => <h1 className="text-xl font-bold mt-3 mb-1">{children}</h1>,
        h2: ({ children }) => <h2 className="text-lg font-bold mt-3 mb-1">{children}</h2>,
        h3: ({ children }) => <h3 className="text-base font-semibold mt-2 mb-1">{children}</h3>,
        p:  ({ children }) => <p className="mb-2 leading-relaxed">{children}</p>,
        ul: ({ children }) => <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>,
        ol: ({ children }) => <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>,
        li: ({ children }) => <li className="ml-2">{children}</li>,
        strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
        em: ({ children }) => <em className="italic">{children}</em>,
        code: ({ children }) => <code className="bg-gray-100 text-gray-800 rounded px-1 py-0.5 text-xs font-mono">{children}</code>,
        pre: ({ children }) => <pre className="bg-gray-100 rounded p-3 overflow-x-auto text-xs font-mono mb-2">{children}</pre>,
        blockquote: ({ children }) => <blockquote className="border-l-2 border-primary-300 pl-3 italic text-gray-600 my-2">{children}</blockquote>,
        table: ({ children }) => <table className="w-full text-sm border-collapse mb-2">{children}</table>,
        th: ({ children }) => <th className="border border-gray-300 px-2 py-1 bg-gray-50 font-semibold text-left">{children}</th>,
        td: ({ children }) => <td className="border border-gray-300 px-2 py-1">{children}</td>,
      }}
    >
      {linkifySessionIds(text)}
    </ReactMarkdown>
  )

  const features = [
    { icon: BookOpen, title: t('featureEventDiscovery'), description: t('featureEventDiscoveryDesc'), to: '/catalog', externalHref: externalCatalogUrl },
    { icon: Award, title: t('featureCertificates'), description: t('featureCertificatesDesc'), to: '/registration-history' },
    { icon: FileText, title: t('featureTranscripts'), description: t('featureTranscriptsDesc'), to: '/transcript' },
    { icon: Users, title: t('featureCommunity'), description: t('featureCommunityDesc'), to: '/community' },
    { icon: TrendingUp, title: t('featureProgress'), description: t('featureProgressDesc'), to: '/user-profile?tab=achievements' },
    { icon: Library, title: t('featureResources'), description: t('featureResourcesDesc'), to: '/community', externalHref: TENANT_LANDING.externalResourcesUrl },
  ]

  const { stats, awards, programs, theme, statsBg, statsDark, regionLogoUrl, watermarkUrl, motto, landingWordmark, statsSolidBar, featureIconHoverClass } = TENANT_LANDING

  return (
    <>
      {/* Hero/Main Content Section */}
      <section
        className={theme
          ? 'relative overflow-hidden w-screen left-1/2 -translate-x-1/2 -mt-4 lg:-mt-8 xl:-mt-10 px-4 sm:px-6 lg:px-8 pt-14 pb-20 lg:pt-20 lg:pb-24'
          : 'relative py-4 px-2 sm:px-4 lg:px-6'}
        style={theme ? { background: theme.heroBackground } : undefined}
      >
        {theme && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0"
            style={{
              backgroundImage: 'radial-gradient(rgba(255, 255, 255, 0.09) 1px, transparent 1px)',
              backgroundSize: '22px 22px',
            }}
          />
        )}
        <div className={theme ? 'relative max-w-5xl mx-auto' : 'max-w-7xl mx-auto'}>
          {/* Welcome Header */}
          {theme ? (
            <div className="text-center mb-12 text-white">
              <div className="mb-10 flex items-center justify-center gap-4 sm:gap-6">
                <img src={BRANDING.appLogo} alt={BRANDING.appName} className="h-9 sm:h-12 w-auto" />
                <span aria-hidden="true" className="text-2xl sm:text-3xl font-light text-white/60">×</span>
                <img src={theme.heroPartner.logoUrl} alt={theme.heroPartner.name} className="h-7 sm:h-9 w-auto" />
              </div>
              <p className="font-jason-sans text-sm md:text-base font-semibold uppercase tracking-[0.2em] text-primary-200 mb-4">
                {isAuthenticated && getUserName() ? `${getGreeting()}, ${getUserName()}` : `${getGreeting()}!`}
              </p>
              <h1 className="font-jason-serif text-4xl md:text-5xl leading-tight max-w-4xl mx-auto">
                {theme.heroHeading}
              </h1>
              <div
                className="mx-auto my-6 h-[3px] w-28 rounded-full"
                style={{ background: `linear-gradient(90deg, ${BRANDING.primaryColor}, ${BRANDING.secondaryColor})` }}
              />
              <p className="font-jason-sans text-lg md:text-xl text-white/90 max-w-3xl mx-auto">
                {theme.heroSubheading}
              </p>
              <Link
                to={theme.heroCta.to}
                className="mt-8 inline-block rounded px-10 py-4 font-jason-sans text-lg font-semibold text-white shadow-lg transition hover:brightness-110 no-underline hover:no-underline focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-transparent"
                style={{ backgroundColor: BRANDING.accentColor || BRANDING.secondaryColor }}
              >
                {theme.heroCta.label}
              </Link>
            </div>
          ) : (
          <div className="text-center mb-8">
            <div className="inline-flex items-center space-x-2 bg-primary-100 text-primary-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Sparkles className={`h-4 w-4 ${BRANDING.accentColor ? 'text-[var(--color-accent)]' : ''}`} />
              <span>{t('aiPoweredPlatform')}</span>
            </div>
            
            {isAuthenticated && getUserName() ? (
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-primary-500">
                {getGreeting()}, {getUserName()}
              </h1>
            ) : (
              <div className="mb-4">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-500">
                  {getGreeting()}!
                </h1>
                <p className="text-2xl md:text-3xl font-semibold text-secondary-500 mt-2">
                  {t('welcomeTo', { appName: 'escDevelopWorks™' })}
                </p>
              </div>
            )}
          </div>
          )}

          {isAuthenticated && <WaitlistExpirationNotices />}

          {isAuthenticated && showInProgressPrompt && (
            <div
              className="rounded-xl border bg-white shadow-sm p-4 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
              style={{ borderColor: 'var(--color-primary)' }}
              role="status"
              data-testid="landing-in-progress-course"
            >
              <div className="flex items-start gap-3">
                <PlayCircle className="h-6 w-6 flex-shrink-0 text-primary-600" />
                <div>
                  <h2 className="text-base font-semibold text-gray-900">{t('inProgressCourseTitle')}</h2>
                  <p className="text-sm text-gray-600">
                    {t('inProgressCourseMessage', { count: inProgressCourseList.length, title: inProgressCourseList[0].title })}
                  </p>
                </div>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={dismissInProgressPrompt}
                  className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  {t('inProgressCourseNotNow')}
                </button>
                <Link
                  to={continueCoursesPath}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-all hover:opacity-90"
                  style={{ backgroundColor: 'var(--color-primary)' }}
                >
                  <PlayCircle className="h-4 w-4" />
                  {t('inProgressCourseContinue')}
                </Link>
              </div>
            </div>
          )}

          {/* tab=courses opens the Online or Canvas tab, whichever holds the participant's courses. */}
          {isAuthenticated && !showInProgressPrompt && (
            <div
              className="rounded-xl border border-gray-300 bg-white shadow-sm p-4 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
              data-testid="landing-my-online-courses"
            >
              <div className="flex items-start gap-3">
                <PlayCircle className="h-6 w-6 flex-shrink-0 text-primary-600" />
                <div>
                  <h2 className="text-base font-semibold text-gray-900">{t('myOnlineCourses')}</h2>
                  <p className="text-sm text-gray-600">{t('myOnlineCoursesDesc')}</p>
                </div>
              </div>
              <Link
                to="/registration-history?tab=courses"
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-all hover:opacity-90 flex-shrink-0"
                style={{ backgroundColor: 'var(--color-primary)' }}
              >
                <PlayCircle className="h-4 w-4" />
                {t('viewMyOnlineCourses')}
              </Link>
            </div>
          )}

          {/* AI Chat Panel */}
          <div className={theme
            ? 'rounded-lg shadow-2xl p-6 lg:p-8 mb-6 bg-white'
            : 'rounded-xl shadow-lg border border-gray-300 p-6 lg:p-8 mb-6 bg-white'}>
            {/* Ask me anything text */}
            {searchHistory.length === 0 && !isSearching && (
              <div className="text-center mb-6">
                <p className="text-sm lg:text-base text-blue-900">{t('unlockPotential')}</p>
              </div>
            )}

            {/* Clear Chat button */}
            {searchHistory.length > 0 && (
              <div className="flex justify-end mb-4">
                <button
                  onClick={handleClearChat}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-maroon-600 hover:text-maroon-700 hover:bg-maroon-50 rounded-lg transition-colors border border-maroon-200 hover:border-maroon-300 shadow-sm"
                  title={t('clearConversation')}
                >
                  <Trash2 className="h-4 w-4" />
                  <span className="hidden sm:inline">{t('clearChat')}</span>
                </button>
              </div>
            )}

            {/* Chat history */}
            <div 
              ref={scrollContainerRef}
              onScroll={handleScroll}
              className="space-y-4 max-h-[400px] overflow-y-auto scroll-smooth mb-6"
            >
              {searchHistory.map((item, index) => (
                <div key={index} className="space-y-4">
                  {/* User query */}
                  <div className="flex items-start space-x-2 justify-end">
                    <div className="flex-1 max-w-3xl">
                      <div className="bg-primary-500 text-white rounded-2xl px-4 py-3 shadow-md relative"
                        style={{
                          borderTopRightRadius: '4px',
                        }}
                      >
                        <div className="absolute -right-1 top-2 w-3 h-3 bg-primary-500 transform rotate-45"></div>
                        <p className="text-sm leading-relaxed relative z-10">{item.query}</p>
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <div className="h-8 w-8 bg-primary-500 rounded-full flex items-center justify-center shadow-md">
                        <svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* AI response */}
                  <div className="flex items-start space-x-2">
                    <div className="flex-shrink-0">
                      <div className="h-8 w-8 bg-gradient-to-br from-primary-500 to-secondary-600 rounded-full flex items-center justify-center shadow-md">
                        <Sparkles className={`h-4 w-4 ${BRANDING.accentColor ? 'text-[var(--color-accent)]' : 'text-white'}`} />
                      </div>
                    </div>
                    <div className="flex-1 max-w-3xl">
                      <div className="bg-white border border-gray-200 rounded-2xl px-4 py-3 shadow-md relative"
                        style={{
                          borderTopLeftRadius: '4px',
                        }}
                      >
                        <div className="absolute -left-1 top-2 w-3 h-3 bg-white border-l border-t border-gray-200 transform rotate-45"></div>
                        <div className="text-sm text-gray-800 leading-relaxed relative z-10">
                          {item.response ? (
                            <>
                              {renderResponseWithLinks(item.response)}
                              {item.isStreaming && item.response && (
                                <span className="inline-block w-0.5 h-4 bg-primary-500 ml-1 animate-pulse"></span>
                              )}
                            </>
                          ) : (
                            <div className="flex items-center space-x-2 py-2">
                              <div className="flex space-x-1">
                                <div className="w-2 h-2 bg-primary-500 rounded-full animate-[bounce_1s_ease-in-out_infinite]"></div>
                                <div className="w-2 h-2 bg-primary-500 rounded-full animate-[bounce_1s_ease-in-out_0.2s_infinite]"></div>
                                <div className="w-2 h-2 bg-primary-500 rounded-full animate-[bounce_1s_ease-in-out_0.4s_infinite]"></div>
                              </div>
                              <span className="text-xs text-gray-500">{t('thinking')}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              <div ref={resultsEndRef} />
            </div>

            {/* Search input */}
            <div>
              <form onSubmit={handleSearch} className="relative">
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                  <Sparkles className={`h-5 w-5 ${BRANDING.accentColor ? 'text-[var(--color-accent)]' : 'text-primary-500'}`} />
                </div>
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('askAboutCourses')}
                  className={`w-full pl-12 pr-12 py-3 bg-white border-2 border-gray-300 rounded-xl focus:outline-none shadow-sm text-sm lg:text-base text-gray-900 placeholder:text-gray-500 ${BRANDING.accentColor ? 'focus:ring-2 focus:ring-[var(--color-accent)] focus:border-[var(--color-accent)]' : 'focus:ring-2 focus:ring-primary-500 focus:border-transparent'}`}
                  disabled={isSearching}
                />
                {isSearching && (
                  <div className="absolute right-14 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary-500 border-t-transparent"></div>
                  </div>
                )}
                <button
                  type="submit"
                  disabled={isSearching || !searchQuery.trim()}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors shadow-md"
                >
                  <ArrowUp className="h-5 w-5" />
                </button>
              </form>
              
              {/* Suggestions */}
              {searchHistory.length === 0 && !isSearching && (
                <div className="mt-3 flex flex-wrap gap-2 justify-center">
                  {(theme?.searchSuggestions ?? [
                    "Help make my campus safer?",
                    "¿Cómo obtengo mi certificado de ESL?",
                    "如何获得我的ESL证书？",
                    "How to better control classroom behavior.",
                    "Find sessions after 1/1/2027"
                  ]).map((suggestion, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSearchQuery(suggestion)}
                      className="text-xs lg:text-sm font-medium text-gray-700 hover:text-primary-700 bg-white hover:bg-primary-50 px-3 py-1.5 rounded-full border-2 hover:border-primary-400 transition-all shadow-sm hover:shadow-md"
                      style={{ borderColor: '#F4A79D' }}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Recommended Sessions Carousel */}
          {isAuthenticated && enableRecommendations && (
            <div className={navyLanding
              ? 'bg-slate-50 rounded-lg border border-slate-200 p-4 mb-6'
              : 'bg-gradient-to-r from-primary-50 to-secondary-50 rounded-lg shadow-md border border-primary-200 p-4 mb-6'}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${navyLanding ? 'bg-ar-navy' : 'bg-primary-500'}`}>
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className={`text-lg font-bold ${navyLanding ? 'text-ar-navy' : 'text-gray-900'}`}>{t('aiPoweredRecommendations')}</h2>
                    {!isCarouselCollapsed && (
                      <p className="text-xs text-gray-600 hidden lg:block">{t('personalizedCourses')}</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-1">
                  {!isCarouselCollapsed && (
                    <>
                      <button
                        onClick={() => scroll('left')}
                        disabled={!showLeftArrow}
                        className="p-2 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm border border-gray-300"
                      >
                        <ChevronLeft className="h-5 w-5 text-gray-700" />
                      </button>
                      <button
                        onClick={() => scroll('right')}
                        disabled={!showRightArrow}
                        className="p-2 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm border border-gray-300"
                      >
                        <ChevronRight className="h-5 w-5 text-gray-700" />
                      </button>
                    </>
                  )}
                  <button
                    onClick={toggleCarouselCollapse}
                    className="p-2 rounded-lg bg-white hover:bg-gray-50 transition-all shadow-sm border border-gray-300"
                    title={isCarouselCollapsed ? t('expandRecommendations') : t('minimizeRecommendations')}
                  >
                    {isCarouselCollapsed ? (
                      <ChevronDown className="h-5 w-5 text-gray-700" />
                    ) : (
                      <ChevronUp className="h-5 w-5 text-gray-700" />
                    )}
                  </button>
                </div>
              </div>
              
              {!isCarouselCollapsed && (loadingRecommendations ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-transparent border-t-primary-500"></div>
                </div>
              ) : recommendations.length > 0 ? (
                <div
                  ref={carouselRef}
                  className="flex gap-4 overflow-x-auto scrollbar-hide scroll-smooth pb-1"
                  style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                  {recommendations.map((session) => {
                    const categoryColors = getCategoryColor(session.category)
                    return (
                    <div
                      key={session.sessionId}
                      onClick={() => navigate(`/session/${session.sessionId}`)}
                      className="flex-none w-72 bg-white border border-gray-300 rounded-lg p-4 hover:shadow-lg hover:scale-[1.02] transition-all cursor-pointer group"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <span className={`text-xs font-semibold ${categoryColors.text} ${categoryColors.bg} px-2 py-1 rounded-full`}>
                          {session.category}
                        </span>
                      </div>
                      <h3 className="text-sm font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {session.title}
                      </h3>
                      <div className="space-y-1 text-xs text-gray-600">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="h-3.5 w-3.5 text-primary-500 flex-shrink-0" />
                          <span className="truncate">{formatDate(session.startDate)}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Clock className="h-3.5 w-3.5 text-primary-500 flex-shrink-0" />
                          <span className="truncate">{formatTime(session.startDate, session.endDate)}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <MapPin className="h-3.5 w-3.5 text-maroon-500 flex-shrink-0" />
                          <span className="line-clamp-1">{session.roomName}</span>
                        </div>
                      </div>
                    </div>
                  )})}
                </div>
              ) : (
                <div className="text-center py-6 text-gray-500 text-sm">
                  <p>{t('noRecommendations')}</p>
                </div>
              ))}
            </div>
          )}

        </div>
      </section>

      {theme && theme.ticker.length > 0 && (
        <div
          className="w-screen relative left-1/2 -translate-x-1/2 overflow-hidden py-3"
          style={{ background: theme.tickerBackground }}
        >
          <div className="landing-ticker-track flex w-max">
            {[0, 1].map((copy) => (
              <ul key={copy} className="flex shrink-0" aria-hidden={copy === 1 || undefined}>
                {theme.ticker.map((item) => (
                  <li key={item} className="flex items-center font-jason-sans text-lg md:text-xl font-semibold uppercase tracking-wide text-white whitespace-nowrap">
                    <span className="px-6">{item}</span>
                    <span className="text-white/70">|</span>
                  </li>
                ))}
              </ul>
            ))}
          </div>
        </div>
      )}

      {programs ? (
      /* Programs Section — replaces generic features for tenants with their own curricula */
      <section className="py-20 bg-white w-screen relative left-1/2 -translate-x-1/2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className={`text-4xl mb-4 ${theme ? 'font-jason-sans font-bold text-gray-700' : 'font-bold text-gray-900'}`}>{programs.heading}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">{programs.subheading}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {programs.items.map((program) => (
              <a
                key={program.href}
                href={program.href}
                target="_blank"
                rel="noopener noreferrer"
                className={`group flex flex-col overflow-hidden bg-white transition-all duration-300 transform hover:-translate-y-1 hover:shadow-xl no-underline hover:no-underline focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${theme ? 'rounded-lg shadow-md' : 'rounded-2xl border border-gray-300 hover:border-primary-300'}`}
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
                  <img
                    src={program.imageUrl}
                    alt=""
                    loading="lazy"
                    className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                  />
                  {theme && (
                    <>
                      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/10 to-transparent" />
                      <h3 className="absolute left-5 top-4 right-[32%] font-jason-serif text-2xl leading-snug text-white drop-shadow">
                        {program.title}
                      </h3>
                    </>
                  )}
                </div>
                <div className={`flex flex-1 flex-col p-6 ${theme ? 'font-jason-sans' : ''}`}>
                  {!theme && <h3 className="text-xl font-bold text-gray-900 mb-3">{program.title}</h3>}
                  <p className={`leading-relaxed flex-1 ${theme ? 'text-lg text-gray-700' : 'text-gray-600'}`}>{program.description}</p>
                  <span className={`mt-4 inline-flex items-center gap-1 font-semibold text-secondary-600 ${theme ? 'text-base uppercase tracking-wide' : 'text-sm'}`}>
                    {t('learnMore')}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>
      ) : (
      /* Features Section */
      <section className="py-20 bg-white w-screen relative left-1/2 -translate-x-1/2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className={`text-4xl font-bold mb-4 ${navyLanding ? 'text-ar-navy' : 'text-gray-900'}`}>
              {t('everythingYouNeed')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('comprehensiveTools')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon
              const cardClasses = 'group p-8 rounded-2xl bg-white hover:bg-gradient-to-br hover:from-primary-50 hover:to-secondary-50 border border-gray-300 hover:border-primary-300 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-xl no-underline hover:no-underline'
              const cardContent = (
                <>
                  <div className={`inline-flex items-center justify-center h-14 w-14 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 text-white mb-6 group-hover:scale-110 shadow-md ${featureIconHoverClass ? `transition-all duration-300 ${featureIconHoverClass}` : 'transition-transform'}`}>
                    <Icon className="h-7 w-7" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </>
              )

              if ('externalHref' in feature && feature.externalHref) {
                return (
                  <a
                    key={index}
                    href={feature.externalHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${cardClasses} block cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2`}
                  >
                    {cardContent}
                  </a>
                )
              }

              if ('to' in feature && feature.to) {
                return (
                  <Link
                    key={index}
                    to={feature.to}
                    className={`${cardClasses} block cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2`}
                  >
                    {cardContent}
                  </Link>
                )
              }

              return (
                <div key={index} className={cardClasses}>
                  {cardContent}
                </div>
              )
            })}
          </div>
        </div>
      </section>
      )}

      {/* Stats Section */}
      {awards.length > 0 ? (
        /* Awards strip — replaces stats for tenants with recognition badges */
        <div className="w-screen relative left-1/2 -translate-x-1/2">
          <section
            className="overflow-hidden relative"
            style={{
              backgroundColor: statsBg,
              ...(BRANDING.accentColor ? { borderBottom: `2px solid ${BRANDING.accentColor}` } : {}),
            }}
          >
            <div className="relative max-w-7xl mx-auto px-8 py-10">
              <h2 className={`text-center mb-8 ${theme ? 'font-jason-serif text-3xl md:text-4xl' : 'text-2xl md:text-3xl font-bold'} ${statsDark ? 'text-white' : 'text-primary-700'}`}>
                {t('awardsHeading')}
              </h2>
              <ul className="flex flex-wrap justify-center gap-x-6 gap-y-6">
                {awards.map((award) => {
                  const badge = (
                    <img
                      src={award.imageUrl}
                      alt={award.label}
                      title={award.label}
                      className="h-28 w-28 md:h-32 md:w-32 object-contain transition-transform duration-200 hover:scale-105"
                    />
                  )
                  return (
                    <li key={award.imageUrl}>
                      {award.href ? (
                        <a href={award.href} target="_blank" rel="noopener noreferrer">{badge}</a>
                      ) : badge}
                    </li>
                  )
                })}
              </ul>
            </div>
          </section>
        </div>
      ) : statsDark ? (
        /* Dark-strip layout (R4) */
        <div className="w-screen relative left-1/2 -translate-x-1/2">
          {landingWordmark ? (
            <div className="flex flex-col items-center py-10">
              <span className="font-display text-2xl font-extrabold tracking-tight text-ar-navy">
                {landingWordmark.primary} <span className="text-ar-red">{landingWordmark.accent}</span>
              </span>
            </div>
          ) : !statsSolidBar && (
            <div className="flex justify-center py-4 bg-white">
              <img src={regionLogoUrl} alt={t('regionLogoAlt')} className="h-12 w-auto" />
            </div>
          )}
          {stats.length > 0 && (
          <section
            className="overflow-hidden relative"
            style={{
              backgroundColor: statsBg,
              ...(BRANDING.accentColor ? { borderBottom: `2px solid ${BRANDING.accentColor}` } : {}),
            }}
          >
            {watermarkUrl && (
              <div className="absolute left-8 top-0 bottom-0 flex items-center pointer-events-none select-none">
                <img src={watermarkUrl} alt="" className="h-full w-auto opacity-30" />
              </div>
            )}
            <div className="relative max-w-7xl mx-auto px-8 py-8 flex flex-wrap justify-center gap-x-4 gap-y-6">
              {stats.map((stat, index) => (
                <div key={index} className="text-center px-4 py-2 w-32 sm:w-36">
                  <div className="text-3xl md:text-4xl font-bold text-white mb-1">{stat.value}</div>
                  <div className={`text-xs font-semibold uppercase tracking-widest ${BRANDING.accentColor ? 'text-white' : 'text-white/60'}`}>{stat.label}</div>
                </div>
              ))}
            </div>
          </section>
          )}
        </div>
      ) : (
        /* Light icon layout */
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col items-center mb-8 gap-3">
              <img src={regionLogoUrl} alt={t('regionLogoAlt')} className="h-28 w-auto" />
              {motto && (
                <p className="text-2xl font-bold tracking-wide">
                  {motto.split(' ').map((word, i) => {
                    const colors = ['text-primary-700', 'text-green-700', 'text-secondary-500']
                    return <span key={i} className={colors[i % colors.length]}>{word} </span>
                  })}
                </p>
              )}
            </div>
            <div className="flex flex-wrap justify-center gap-x-8 gap-y-8 md:gap-x-12">
              {stats.map((stat, index) => {
                const Icon = stat.icon
                return (
                  <div key={index} className="text-center w-32 sm:w-36">
                    {Icon && <Icon className="w-6 h-6 text-secondary-500 mx-auto mb-2" />}
                    <div className="text-4xl md:text-5xl font-bold text-primary-700 mb-1">
                      {stat.value}
                    </div>
                    <div className="text-primary-800 font-semibold">{stat.label}</div>
                  </div>
                )
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      {!isAuthenticated && theme && (
        <section className="py-16 w-screen relative left-1/2 -translate-x-1/2">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div
              className="max-w-5xl mx-auto rounded-2xl shadow-xl px-8 py-12 md:px-12 text-center text-white"
              style={{ backgroundColor: BRANDING.secondaryColor }}
            >
              <h2 className="font-jason-serif text-3xl md:text-4xl mb-4">
                {t('readyToStart')}
              </h2>
              <p className="font-jason-sans text-lg text-white/90 mb-8 max-w-xl mx-auto">
                {t('joinThousands')}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 font-jason-sans">
                <button
                  onClick={() => navigate('/register')}
                  className="w-full sm:w-auto bg-white hover:bg-gray-100 px-8 py-3.5 rounded font-semibold text-base transition-colors shadow-md"
                  style={{ color: BRANDING.secondaryColor }}
                >
                  {t('createFreeAccount')}
                </button>
                <button
                  onClick={() => navigate('/login')}
                  className="w-full sm:w-auto bg-transparent hover:bg-white/10 text-white px-8 py-3.5 rounded font-semibold text-base border-2 border-white transition-colors"
                >
                  {t('login')}
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {!isAuthenticated && !theme && (
        <section className="py-16 w-screen relative left-1/2 -translate-x-1/2">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl mx-auto bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-12 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-700 mb-4">
                {t('readyToStart')}
              </h2>
              <p className="text-gray-500 text-lg mb-8 max-w-xl mx-auto">
                {t('joinThousands')}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <button
                  onClick={() => navigate('/register')}
                  className="w-full sm:w-auto bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white px-8 py-3.5 rounded-lg font-semibold text-base transition-all shadow-md hover:shadow-lg"
                >
                  {t('createFreeAccount')}
                </button>
                <button
                  onClick={() => navigate('/login')}
                  className="w-full sm:w-auto bg-white hover:bg-gray-50 text-gray-700 px-8 py-3.5 rounded-lg font-semibold text-base border-2 border-gray-300 transition-all"
                >
                  {t('login')}
                </button>
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  )
}
